/**
 * Oś czasu kroków nakładki `MultiStepLoader`, trzymana poza Reactem, bo to
 * jedyna nietrywialna część tego komponentu i jedyna, którą da się sprawdzić
 * testem: maszynka na `setTimeout` plus dwie czyste funkcje, które mówią,
 * co i kiedy.
 */

export interface StepProgress {
  index: number;
  /** Ostatni krok został pokazany jako zrobiony — koniec osi. */
  finished: boolean;
}

export const INITIAL_STEP_PROGRESS: StepProgress = {
  index: 0,
  finished: false,
};

/** Odstęp między krokami dopóki praca trwa. */
export const DEFAULT_STEP_DURATION = 450;
/** Tempo doganiania po sukcesie — reszta kroków ma tylko mignąć. */
export const DEFAULT_CATCH_UP_DURATION = 180;
/** Ile ostatni krok („Gotowe…”) zostaje na ekranie, zanim oddamy sterowanie. */
export const DEFAULT_LAST_STEP_HOLD = 600;

interface StepTimeline {
  stepCount: number;
  /** Praca w tle się udała — dopiero to zdejmuje blokadę z kroku postojowego. */
  done: boolean;
  stepDuration: number;
  catchUpDuration: number;
  lastStepHold: number;
}

/**
 * Krok, na którym oś czeka na odpowiedź serwera — przedostatni, bo ostatni
 * mówi „gotowe” i nie wolno go pokazać, zanim naprawdę jest gotowe.
 */
export const parkStepIndex = (stepCount: number): number =>
  Math.max(0, stepCount - 2);

export const advanceStep = (
  progress: StepProgress,
  { stepCount, done }: Pick<StepTimeline, 'stepCount' | 'done'>,
): StepProgress => {
  if (progress.finished) return progress;

  if (!done && progress.index >= parkStepIndex(stepCount)) {
    return progress;
  }
  if (progress.index >= stepCount - 1) {
    return { ...progress, finished: true };
  }
  return { index: progress.index + 1, finished: false };
};

/**
 * Odstęp do następnego przeskoku albo `null`, gdy oś nie ma się czym ruszyć
 * (postój albo koniec).
 *
 * Sukces przed krokiem postojowym **nie** przyspiesza tempa i to jest cały
 * mechanizm „minimalnego czasu”: backend odpowiadający w 300 ms i tak nie
 * przewinie żartów, zanim ktokolwiek zdąży je przeczytać.
 */
export const nextTickDelay = (
  progress: StepProgress,
  timeline: StepTimeline,
): number | null => {
  const { stepCount, done, stepDuration, catchUpDuration, lastStepHold } =
    timeline;

  if (progress.finished) return null;

  const parked = progress.index >= parkStepIndex(stepCount);
  if (parked && !done) return null;
  if (progress.index >= stepCount - 1) return lastStepHold;

  return parked ? catchUpDuration : stepDuration;
};

export interface StepProgressRunner {
  /** Zgłasza sukces pracy w tle; zdejmuje postój. */
  markDone: () => void;
  stop: () => void;
}

export interface StepProgressOptions {
  stepCount: number;
  stepDuration?: number;
  catchUpDuration?: number;
  lastStepHold?: number;
  onStep: (index: number) => void;
  onFinished?: () => void;
}

export const runStepProgress = ({
  stepCount,
  stepDuration = DEFAULT_STEP_DURATION,
  catchUpDuration = DEFAULT_CATCH_UP_DURATION,
  lastStepHold = DEFAULT_LAST_STEP_HOLD,
  onStep,
  onFinished,
}: StepProgressOptions): StepProgressRunner => {
  let progress = INITIAL_STEP_PROGRESS;
  let done = false;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const timeline = (): StepTimeline => ({
    stepCount,
    done,
    stepDuration,
    catchUpDuration,
    lastStepHold,
  });

  const schedule = () => {
    if (stopped) return;

    const delay = nextTickDelay(progress, timeline());
    if (delay === null) return;

    timer = setTimeout(() => {
      timer = null;
      const next = advanceStep(progress, { stepCount, done });
      const movedTo = next.index !== progress.index ? next.index : null;
      const justFinished = next.finished && !progress.finished;
      progress = next;

      if (movedTo !== null) onStep(movedTo);
      if (justFinished) onFinished?.();

      schedule();
    }, delay);
  };

  schedule();

  return {
    markDone: () => {
      if (done || stopped) return;
      done = true;
      // Na postoju nic nie jest zaplanowane, więc oś trzeba obudzić ręcznie.
      if (timer === null) schedule();
    },
    stop: () => {
      stopped = true;
      if (timer !== null) clearTimeout(timer);
      timer = null;
    },
  };
};
